import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ordersApi, paymentApi } from '../services/api';
import { Order } from '../types';
import { useAuth } from '../context/AuthContext';

export default function PaymentCancelledPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await ordersApi.get(orderId);
        // API returns order directly or in response.data
        setOrder(response.data || response);
      } catch (err: any) {
        console.error('Failed to fetch order:', err);
        setError(err.message || 'Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const handleRetryPayment = async () => {
    if (!order || !user) return;

    setRetrying(true);
    try {
      // Get the order data to retry payment
      const orderData = order.orderData || order.order_data;
      let parsedOrderData: any = {};

      if (orderData) {
        if (typeof orderData === 'string') {
          try {
            parsedOrderData = JSON.parse(orderData);
          } catch (e) {
            console.error('Failed to parse order data:', e);
          }
        } else {
          parsedOrderData = orderData;
        }
      }

      // Create new checkout session for existing order
      const response = await paymentApi.createCheckout({
        existingOrderId: orderId,
        dealer: {
          id: (user as any).id || (user as any).contactId,
          dealerId: (user as any).dealerId,
          email: user.email || '',
          company: (user as any).companyName || (user as any).company || '',
          contactName: user.name || '',
        },
        order: parsedOrderData.order || {},
        invoice: parsedOrderData.invoice || {},
        paymentAmount: parseFloat(String(order.totalAmount || order.total_amount)) * 100 * 0.5, // 50% deposit
        paymentMethodType: 'card',
      });

      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        setError('Failed to create payment session');
        setRetrying(false);
      }
    } catch (err: any) {
      console.error('Failed to retry payment:', err);
      setError(err.message || 'Failed to create payment session');
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-keder-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-keder-yellow mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-keder-dark flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Order</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/dashboard"
            className="inline-block px-6 py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number | string | undefined) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (num === undefined || isNaN(num)) return '$0.00';
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-keder-dark py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Cancelled Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-orange-900/30 flex items-center justify-center">
            <svg className="w-10 h-10 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Payment Cancelled</h1>
          <p className="text-gray-400">Your order has been saved. You can complete payment anytime.</p>
        </div>

        {/* Order Details Card */}
        {order && (
          <div className="bg-keder-gray rounded-xl border border-gray-700 p-6 mb-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-gray-400 text-sm">Order Number</p>
                <p className="text-2xl font-bold text-keder-yellow">{order.orderNumber || order.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Status</p>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-orange-900/30 text-orange-400">
                  Awaiting Payment
                </span>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Sidemark / Job Name</p>
                  <p className="text-white font-medium">{order.sidemark || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">PO Number</p>
                  <p className="text-white font-medium">{order.poNumber || order.po_number || '-'}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <div className="flex justify-between items-center">
                <p className="text-gray-400">Order Total</p>
                <p className="text-xl font-bold text-white">{formatCurrency(order.totalAmount || order.total_amount)}</p>
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-gray-400">Minimum Deposit (50%)</p>
                <p className="text-lg font-medium text-orange-400">
                  {formatCurrency((parseFloat(String(order.totalAmount || order.total_amount)) || 0) * 0.5)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-5 mb-6">
          <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Your Order is Saved
          </h3>
          <ul className="text-gray-300 text-sm space-y-2">
            <li className="flex items-start gap-2">
              <svg className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Your order details have been saved and won't be lost</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You can complete payment from the order details page anytime</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Production will begin once payment is received</span>
            </li>
          </ul>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleRetryPayment}
            disabled={retrying}
            className="flex-1 px-6 py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {retrying ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Creating Payment...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Try Payment Again
              </>
            )}
          </button>
          <Link
            to={`/orders/${orderId}`}
            className="flex-1 px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors text-center"
          >
            View Order
          </Link>
        </div>

        <div className="mt-4">
          <Link
            to="/dashboard"
            className="block w-full px-6 py-3 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:bg-gray-800 transition-colors text-center"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
